import {Box, P} from 'exsportia-components'
import {useFormikContext} from 'formik'
import {DateTime} from 'luxon'
import React, {FC, useCallback, useEffect, useMemo} from 'react'
import {useQuery} from 'react-query'
import {useNavigate} from 'react-router-dom'

import {prepareCyclesForShow} from '../../api/subscription-bookings/helpers'
import {useSubscriptionBooking} from '../../api/subscription-bookings/hooks'
import {getSubscriptionPromoSummary} from '../../api/subscription-bookings/promo-summary'
import {SubscriptionBookingStatusesEnum} from '../../api/subscription-bookings/types'
import {SUBSCRIPTION_PRICE_TYPE_MAP} from '../../api/subscriptions/constants'
import {useSubscription} from '../../api/subscriptions/hooks'
import {PaymentMethod} from '../../api/user/types'
import {useVenue} from '../../api/venue/hooks'
import {PricingDetails, PricingElementType} from '../../components/pricing-details/pricing-details'
import {usePromoText} from '../../components/promo-code-field/use-promo-text'
import {SubscriptionPreviewCard} from '../../components/subscription-preview-card/subscription-preview-card'
import {noop, safeRound} from '../../helpers/logic'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {TranslatorScope} from '../../locale/types'
import {
  CyclePayTypeEnum,
  PaySubscriptionCycleFormType,
} from '../../services/subscription-bookings/forms/pay-subscription-cycles-form'
import {UserService} from '../../services/user'
import {Button} from '../../ui/components/button/button'
import {DividerWithContent} from '../../ui/components/divider-with-content/divider-with-content'
import {GradientLoader} from '../../ui/components/gradient-loader/gradient-loader'
import {LabeledValueRow} from '../../ui/components/key-value-row/labeled-value-row'
import {PaymentMethodItem} from '../../ui/components/payment-method-item/payment-method-item'
import {TitledSection} from '../../ui/components/titeled-section/titled-section'
import {useLocale} from '../../utils/i18n'
import {useModalActions} from '../../utils/modals'
import {TabbedHeader} from './components/header'
import {InvoicesList} from './components/invoices-list'
import {buildChips} from './utils'

// eslint-disable-next-line
export const SubscriptionBookingBillingPage: FC = () => {
  const {l} = useLocale()
  const navigate = useNavigate()
  const {values, setFieldValue} = useFormikContext<PaySubscriptionCycleFormType>()
  const {data: venue, isFetching: isVenueFetching} = useVenue()
  const {data: subscriptionBooking, isFetching: isSubscriptionBookingFetching} = useSubscriptionBooking(values.subscriptionBooking?.uid)
  const {data: subscription, isFetching: isSubscriptionFetching} = useSubscription(subscriptionBooking?.membershipId)
  const {data: paymentMethods} = useQuery(UserService.queries.getUserPaymentMethodsQuery({
    params: {venueId: venue?.uid},
    refetchOnWindowFocus: false,
  }))
  const selectedPaymentMethod: PaymentMethod | null = (paymentMethods?.paymentMethods && subscriptionBooking)
    ? paymentMethods.paymentMethods.find(method => method.uid === subscriptionBooking.paymentMethodId) || null
    : null

  useEffect(() => {
    setFieldValue('cyclePayType', null).catch(e => console.log(String(e)))
  }, [])

  const navigateToSaveCard = useCallback(() => {
    navigate('../set-payment-method')
    setFieldValue('cyclePayType', CyclePayTypeEnum.SAVE_CARD).catch(e => console.log(String(e)))
  }, [])


  const navigateToPayCycle = useCallback(() => {
    navigate('../pay-cycle')
    setFieldValue('cyclePayType', CyclePayTypeEnum.PAY_NOW).catch(e => console.log(String(e)))
  }, [])

  const cycles = useMemo(() => {
    if (!subscriptionBooking || !venue) {
      return []
    }
    return prepareCyclesForShow(
      subscriptionBooking.cycles,
      venue.timeZone
    )
  }, [venue, subscriptionBooking])
  const rolling = subscription?.rolling ?? false

  const {money, discountValue} = usePromoText(venue?.currency)
  const promoLines = useMemo(() => {
    const promo = getSubscriptionPromoSummary(subscriptionBooking)
    if (!promo) {
      return []
    }
    const discount = discountValue(promo)
    const lines = [
      `${l('promo_line', {code: promo.code})} · ${l(
        promo.firstPaymentOnly ? 'promo_first_payment_off' : 'promo_every_payment_off',
        {discount},
      )}`,
    ]
    if (promo.saved > 0) {
      lines.push(l('promo_saved_so_far', {amount: money(promo.saved)}))
    }
    if (promo.next) {
      const next = l('promo_next_payment', {
        price: money(promo.next.price),
        date: DateTime.fromMillis(promo.next.startDate).toLocaleString(DateTime.DATE_MED),
      })
      lines.push(`${next} · ${l(promo.next.discounted ? 'promo_next_with_code' : 'promo_next_full_price')}`)
    }
    return lines
  }, [subscriptionBooking, discountValue, money, l])


  const cycleSummarySettings = useMemo(() => {
    const purchaseFee = subscriptionBooking?.additionalFee || 0
    const isPaidAdditionalFee = subscriptionBooking?.isPaidAdditionalFee
    const currency = venue?.currency
    const currencyLabel = l(`${currency}_symbol` as TranslatorScope)

    const totalRemaining = cycles.reduce<number>((accum, el) => {
      if (!el.isPaid) {
        return safeRound(accum + el.price)
      }
      return accum
    }, isPaidAdditionalFee ? 0 : purchaseFee)
    const outstandingBalance = cycles.reduce((accum, el) => {
      if (el.showInBilling && !el.isPaid) {
        return safeRound(accum + el.price)
      }
      return accum
    }, isPaidAdditionalFee ? 0 : purchaseFee)
    const totalPaid = cycles.reduce((accum, el) => {
      if (el.isPaid) {
        return safeRound(accum + el.price)
      }
      return accum
    }, isPaidAdditionalFee ? purchaseFee : 0)

    const temp = [
      {
        text: l('paid_in_total', {caseAction: 'capitalize'}),
        price: currencyLabel ? totalPaid : 0
      },
      {
        text: l('outstanding_balance', {caseAction: 'capitalize'}),
        price: currencyLabel ? outstandingBalance : 0
      },
    ]
    if (!rolling) {
      temp.push({
        text: l('total_remaining', {caseAction: 'capitalize'}),
        price: currencyLabel ? totalRemaining : 0
      })
    }
    return temp satisfies PricingElementType[]
  }, [venue, subscriptionBooking, subscription])

  const summarySettings = useMemo(() => {
    if (!subscriptionBooking) {
      return []
    }
    const allCyclesPaid = subscriptionBooking.cycles.every(el => el.isPaid)
    const currency = venue?.currency
    const currencyLabel = l(`${currency}_symbol` as TranslatorScope)
    const rawCycles = subscriptionBooking.cycles
    const priceTime = subscriptionBooking.priceTime
    const defaultCyclePrice = subscriptionBooking.defaultCyclePrice
    const cycleToPay = cycles.map((el, index) => ({
      ...el, elIndex: index + 1
    })).find(cycle => !cycle.isPaid)
    const recurringPayment = defaultCyclePrice
      ? defaultCyclePrice
      : rawCycles[0]?.price || 0
    const lastPaidCycle = rawCycles[
      (allCyclesPaid || !cycleToPay)
        ? rawCycles.length - 1
        : cycleToPay.elIndex - 2
      ]

    const result = [
      {
        title: l('billing_interval', {caseAction: 'capitalize'}),
        value: `${SUBSCRIPTION_PRICE_TYPE_MAP[priceTime].label} ${l('payment', {
          caseAction: 'lowerCase'
        })}`
      },
      {
        title: l('recurring_payment', {caseAction: 'capitalize'}),
        value: `${currencyLabel}${recurringPayment}`
      },
    ]
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (lastPaidCycle?.payDate) {
      const lastPayment = lastPaidCycle.payDate
        ? DateTime.fromMillis(lastPaidCycle.payDate).toLocaleString(DateTime.DATE_MED)
        : '-'
      result.push({
        title: l('last_payment', {caseAction: 'capitalize'}),
        value: lastPayment
      })
    }
    if (cycleToPay?.startDate) {
      const nextPayment = (!allCyclesPaid)
        ? DateTime.fromMillis(cycleToPay.startDate).toLocaleString(DateTime.DATE_MED)
        : '-'
      result.push({
        title: l('next_payment', {caseAction: 'capitalize'}),
        value: nextPayment
      })
    }

    return result
  }, [venue, subscriptionBooking, subscription])

  const isLoading = isVenueFetching || isSubscriptionFetching || isSubscriptionBookingFetching

  const chips = useMemo(() => {
    return buildChips(subscriptionBooking, l)
  }, [subscriptionBooking, l])

  const isActive = subscriptionBooking?.status === SubscriptionBookingStatusesEnum.ACTIVE
  const isFullyPaid = useMemo(() => {
    const cyclesArr = subscriptionBooking?.cycles ?? []
    return cyclesArr.length > 0 && cyclesArr.every((c) => c.isPaid)
  }, [subscriptionBooking])
  const isSubCancelled = subscriptionBooking?.subStatus === SubscriptionBookingStatusesEnum.CANCELLED
  const isSubExpired = subscriptionBooking?.subStatus === SubscriptionBookingStatusesEnum.EXPIRED

  const canUpdateSavedCard = Boolean(isActive) && !isFullyPaid
  const canPayNextCycle = Boolean(!isSubCancelled && !isSubExpired) && !isFullyPaid

  const getUpdateCardDisabledReason = useCallback(() => {
    if (!canUpdateSavedCard) {
      return l('subscription_not_active')
    }
    return ''
  }, [canUpdateSavedCard, l])

  const getPayNextCycleDisabledReason = useCallback(() => {
    if (canPayNextCycle) {
      return ''
    }
    if (isSubCancelled) {
      return l('subscription_cancelled')
    }
    if (isSubExpired) {
      return l('subscription_expired')
    }
    return l('action_not_available')
  }, [canPayNextCycle, isSubCancelled, isSubExpired, l])

  const {openModal, closeModal} = useModalActions()
  const openUnavailableActionModal = useCallback(
    (reason: string) => {
      openModal({
        title: l('unavailable'),
        component: (
          <Box>
            <P>{reason}</P>
          </Box>
        ),
        footerConfig: {
          submitButton: {
            text: l('get_subscription', {caseAction: 'capitalize'}),
            onClick: () => {
              if (venue?.uid && subscriptionBooking?.membershipId) {
                navigate(`/${venue.uid}/subscription/${subscriptionBooking.membershipId}`)
              }
              closeModal()
            },
          },
          cancelButton: {
            text: l('home'),
            onClick: () => {
              navigate(`/${venue?.uid || ''}`)
              closeModal()
            },
          },
        },
      })
    },
    [openModal, closeModal, l, venue, subscriptionBooking, navigate],
  )

  const handleUpdateCardClick = useCallback(() => {
    if (isLoading) {
      return
    }
    if (canUpdateSavedCard) {
      navigateToSaveCard()
      return
    }
    const reason = getUpdateCardDisabledReason()
    if (reason) {
      openUnavailableActionModal(reason)
    }
  }, [isLoading, canUpdateSavedCard, getUpdateCardDisabledReason, openUnavailableActionModal, navigateToSaveCard])

  const handlePayNextCycleClick = useCallback(() => {
    if (isLoading) {
      return
    }
    if (canPayNextCycle) {
      navigateToPayCycle()
      return
    }
    const reason = getPayNextCycleDisabledReason()
    if (reason) {
      openUnavailableActionModal(reason)
    }
  }, [isLoading, canPayNextCycle, getPayNextCycleDisabledReason, openUnavailableActionModal, navigateToPayCycle])

  const tabsSettings = useMemo(() => ([
    {
      text: l('about', {caseAction: 'capitalize'}),
      isActive: false,
      onClick: () => navigate('../profile'),
    },
    {
      text: l('billing', {caseAction: 'capitalize'}),
      isActive: true,
      onClick: noop,
    },
  ]), [])

  return (
    <GridLayout header={<TabbedHeader tabsSettings={tabsSettings} />} withoutPadding={true}>
      <Box>
        <Box
          p={'16px'}
        >
          <Box mb={'16px'}>
            <P
              fontWeight={'bold'}
              fontSize={['h1', 'desktop-h1']}
            >
              {subscription?.name || (<GradientLoader style={{width: '150px'}}/>)}
            </P>
          </Box>
          {
            selectedPaymentMethod
            && (
              <TitledSection title={l('payment_method', {caseAction: 'titleCase'})}>
                <PaymentMethodItem
                  paymentMethod={selectedPaymentMethod}
                  additionalCardLabel={l('payment_via')}
                />
              </TitledSection>
            )
          }
          {
            promoLines.length > 0
            && (
              <TitledSection title={l('promo_card_code')}>
                {promoLines.map((line) => (
                  <P key={line} variant={'footnote'} mb={'4px'}>{line}</P>
                ))}
              </TitledSection>
            )
          }
          <TitledSection title={l('pricing_details', {caseAction: 'titleCase'})}>
            <PricingDetails
              withoutTitle
              withoutTotal
              discount={0}
              secondaryItems
              predefinedLength={3}
              isLoading={isLoading}
              currency={venue?.currency}
              values={cycleSummarySettings}
            />
          </TitledSection>
          <TitledSection title={l('invoices', {caseAction: 'titleCase'})}>
            {
              subscriptionBooking ? (
                <InvoicesList
                  additionalFee={subscriptionBooking.additionalFee}
                  isPaidAdditionalFee={subscriptionBooking.isPaidAdditionalFee}
                  subStatus={subscriptionBooking.subStatus}
                  cycles={cycles}
                  cyclesLength={subscriptionBooking.cycles.length}
                  rolling={rolling}
                />
              ) : <GradientLoader height={'125px'}/>
            }
          </TitledSection>
        </Box>
      </Box>
      <Box p={'16px'}>
        <SubscriptionPreviewCard id={subscriptionBooking?.membershipId} chips={chips} />
        <Box mt={'12px'}>
          {
            summarySettings.map((el, i) => {
              if (summarySettings.length === i + 1) {
                return (
                  <Box key={i}>
                    <LabeledValueRow label={el.title}>
                      {el.value}
                    </LabeledValueRow>
                  </Box>
                )
              }
              return (
                <Box key={i}>
                  <LabeledValueRow label={el.title}>
                    {el.value}
                  </LabeledValueRow>
                  <DividerWithContent />
                </Box>
              )
            })
          }
        </Box>
        <Box mt={'16px'}>
          <Box title={!isLoading && !canUpdateSavedCard ? getUpdateCardDisabledReason() : undefined} style={!isLoading && !canUpdateSavedCard ? {opacity: 0.5, cursor: 'not-allowed'} : undefined}>
            <Button onClick={handleUpdateCardClick} type='secondary' disabled={isLoading} isRightIcon={!isLoading && !canUpdateSavedCard}>
              {l('update_saved_card', {caseAction: 'capitalize'})}
            </Button>
          </Box>
          <Box mt={'16px'} title={!isLoading && !canPayNextCycle ? getPayNextCycleDisabledReason() : undefined} style={!isLoading && !canPayNextCycle ? {opacity: 0.5, cursor: 'not-allowed'} : undefined}>
            <Button onClick={handlePayNextCycleClick} type='secondary' disabled={isLoading} isRightIcon={!isLoading && !canPayNextCycle}>
              {l('pay_next_cycle', {caseAction: 'capitalize'})}
            </Button>
          </Box>
        </Box>
      </Box>
    </GridLayout>
  )
}
